/**
 * Annotation Form Component
 * Two-step form for message classification and emotion/intent labeling
 *
 * Step 1: Message Type Classification (required for all messages)
 * Step 2: Emotion/Intent Labels (only for substantive messages)
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type {
  LabelCreate,
  EmotionValence,
  ConfidenceLevel,
  MessageType,
  ExpectedRouting,
} from '@/types/annotation';
import {
  INTENT_OPTIONS,
  EMOTION_LABELS,
  MESSAGE_TYPE_OPTIONS,
  EXPECTED_ROUTING_OPTIONS,
} from '@/types/annotation';
import { Check, X, HelpCircle, ArrowRight, ArrowLeft, Tag, Heart } from 'lucide-react';

interface AnnotationFormProps {
  onSubmit: (data: LabelCreate) => void;
  onSkip: () => void;
  isSubmitting?: boolean;
  startTime?: number; // For tracking time spent
}

export function AnnotationForm({
  onSubmit,
  onSkip,
  isSubmitting = false,
  startTime,
}: AnnotationFormProps) {
  const { t } = useI18n();
  // Step tracking
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1: Message classification
  const [messageType, setMessageType] = useState<MessageType | ''>('');
  const [expectedRouting, setExpectedRouting] = useState<ExpectedRouting | ''>('');

  // Step 2: Emotion/Intent (only for substantive)
  const [valence, setValence] = useState<EmotionValence | ''>('');
  const [emotionLabel, setEmotionLabel] = useState('');
  const [intents, setIntents] = useState<string[]>([]);
  const [needsSupport, setNeedsSupport] = useState(false);
  const [confidence, setConfidence] = useState<ConfidenceLevel | ''>('');
  const [notes, setNotes] = useState('');

  // Auto-set expected routing when message type changes
  useEffect(() => {
    if (messageType) {
      const option = MESSAGE_TYPE_OPTIONS.find((opt) => opt.value === messageType);
      if (option) {
        setExpectedRouting(option.routing);
      }
    } else {
      setExpectedRouting('');
    }
  }, [messageType]);

  // Check if current message type requires emotion annotation
  const requiresEmotionAnnotation = messageType === 'substantive';

  const handleIntentToggle = (intent: string) => {
    setIntents((prev) =>
      prev.includes(intent) ? prev.filter((i) => i !== intent) : [...prev, intent]
    );
  };

  const handleSubmit = useCallback(() => {
    if (!messageType || !expectedRouting) return;

    // For substantive messages, valence is required
    if (requiresEmotionAnnotation && !valence) return;

    const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : undefined;

    onSubmit({
      message_type: messageType,
      expected_routing: expectedRouting,
      emotion_valence: requiresEmotionAnnotation ? valence || null : null,
      emotion_label: requiresEmotionAnnotation && emotionLabel ? emotionLabel : null,
      intents: requiresEmotionAnnotation ? intents : [],
      needs_support: requiresEmotionAnnotation ? needsSupport : false,
      confidence: confidence || null,
      annotator_notes: notes || null,
      time_spent_seconds: timeSpent,
    });
  }, [
    messageType,
    expectedRouting,
    requiresEmotionAnnotation,
    valence,
    emotionLabel,
    intents,
    needsSupport,
    confidence,
    notes,
    startTime,
    onSubmit,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in text fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Global shortcut: Cmd/Ctrl+S to submit (works in both steps when form is valid)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (step === 1 && messageType && expectedRouting && !requiresEmotionAnnotation) {
          // Step 1: Submit directly for non-substantive messages
          handleSubmit();
        } else if (step === 2 && valence) {
          // Step 2: Submit when valence is selected
          handleSubmit();
        }
        return;
      }

      if (step === 1) {
        // Step 1 shortcuts: 1-8 for message types
        const num = parseInt(e.key);
        if (num >= 1 && num <= MESSAGE_TYPE_OPTIONS.length) {
          const option = MESSAGE_TYPE_OPTIONS[num - 1];
          setMessageType(option.value as MessageType);
        }
        // Enter to proceed to step 2 or submit
        if (e.key === 'Enter' && messageType) {
          e.preventDefault();
          if (requiresEmotionAnnotation) {
            setStep(2);
          } else {
            handleSubmit();
          }
        }
      } else if (step === 2) {
        // Step 2 shortcuts
        switch (e.key) {
          case '1':
            setValence('positive');
            break;
          case '2':
            setValence('neutral');
            break;
          case '3':
            setValence('negative');
            break;
          case 'Backspace':
            setStep(1);
            break;
        }
      }

      if (e.key === 'Escape') {
        onSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, messageType, expectedRouting, valence, intents, needsSupport, confidence, notes, onSkip, requiresEmotionAnnotation, handleSubmit]);

  const isStep1Valid = !!messageType && !!expectedRouting;
  const isStep2Valid = !requiresEmotionAnnotation || !!valence;
  const canSubmit = isStep1Valid && isStep2Valid;

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-700">
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            step === 1
              ? 'bg-primary-900/50 text-primary-300 ring-2 ring-primary-700'
              : isStep1Valid
                ? 'bg-green-900/50 text-green-300'
                : 'bg-gray-800 text-gray-400'
          }`}
        >
          <Tag className="w-4 h-4" />
          1. {t('annotationForm.steps.messageClassification')}
          {isStep1Valid && step !== 1 && <Check className="w-4 h-4" />}
        </div>
        <ArrowRight className="w-4 h-4 text-gray-500" />
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            !requiresEmotionAnnotation
              ? 'bg-gray-800 text-gray-500 line-through'
              : step === 2
                ? 'bg-primary-900/50 text-primary-300 ring-2 ring-primary-700'
                : 'bg-gray-800 text-gray-400'
          }`}
        >
          <Heart className="w-4 h-4" />
          2. {t('annotationForm.steps.emotionIntent')}
          {!requiresEmotionAnnotation && <span className="text-xs">({t('annotationForm.steps.skipped')})</span>}
        </div>
      </div>

      {step === 1 && (
        <>
          {/* Message Type Selection */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
            <label className="block text-sm font-medium text-gray-200 mb-2">
              {t('annotationForm.messageType.label')} <span className="text-red-400">*</span>
              <span className="text-gray-400 font-normal ml-2">({t('annotationForm.messageType.shortcutHint')})</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {MESSAGE_TYPE_OPTIONS.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setMessageType(option.value as MessageType)}
                  className={`
                    p-3 rounded-lg border-2 transition-all text-left
                    ${
                      messageType === option.value
                        ? 'border-primary-400 bg-primary-900/30 ring-2 ring-primary-700'
                        : 'border-gray-700 hover:border-gray-600 bg-gray-800'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-medium ${messageType === option.value ? 'text-primary-300' : 'text-gray-200'}`}
                    >
                      {option.label}
                    </span>
                    <span className="text-xs text-gray-400 bg-gray-700 px-1.5 py-0.5 rounded">
                      {index + 1}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{option.description}</div>
                  <div className="text-xs mt-1">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded ${
                        option.routing === 'simple_prompt'
                          ? 'bg-blue-900/50 text-blue-300'
                          : 'bg-orange-900/50 text-orange-300'
                      }`}
                    >
                      {option.routing === 'simple_prompt' ? t('annotationForm.messageType.simplePrompt') : t('annotationForm.messageType.fullAgent')}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Expected Routing (auto-set but can override) */}
          {messageType && (
            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
              <label className="block text-sm font-medium text-gray-200 mb-2">
                {t('annotationForm.expectedRouting.label')} <span className="text-gray-400 font-normal">({t('annotationForm.expectedRouting.autoSetHint')})</span>
              </label>
              <div className="flex gap-3">
                {EXPECTED_ROUTING_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setExpectedRouting(option.value as ExpectedRouting)}
                    className={`
                      flex-1 p-3 rounded-lg border-2 transition-all text-left
                      ${
                        expectedRouting === option.value
                          ? 'border-primary-400 bg-primary-900/30'
                          : 'border-gray-700 hover:border-gray-600 bg-gray-800'
                      }
                    `}
                  >
                    <div
                      className={`font-medium ${expectedRouting === option.value ? 'text-primary-300' : 'text-gray-200'}`}
                    >
                      {option.label}
                    </div>
                    <div className="text-xs text-gray-400">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Info about next step */}
          {messageType && !requiresEmotionAnnotation && (
            <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-4 text-sm text-blue-300">
              <strong>💡</strong> {t('annotationForm.noEmotionHint')}
            </div>
          )}
        </>
      )}

      {step === 2 && requiresEmotionAnnotation && (
        <>
          {/* Emotion Valence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('annotationForm.emotion.valenceLabel')} <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-2">({t('annotationForm.emotion.valenceShortcut')})</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'positive', labelKey: 'positive', color: 'green', emoji: '' },
                { value: 'neutral', labelKey: 'neutral', color: 'gray', emoji: '' },
                { value: 'negative', labelKey: 'negative', color: 'red', emoji: '' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setValence(option.value as EmotionValence)}
                  className={`
                    p-4 rounded-lg border-2 transition-all text-center
                    ${
                      valence === option.value
                        ? `border-${option.color}-500 bg-${option.color}-50 ring-2 ring-${option.color}-200`
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                  `}
                >
                  <div className="text-2xl mb-1">{option.emoji}</div>
                  <div
                    className={`font-medium ${valence === option.value ? `text-${option.color}-700` : 'text-gray-700'}`}
                  >
                    {t(`annotationForm.emotion.${option.labelKey}`)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Specific Emotion Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('annotationForm.emotion.specificLabel')} <span className="text-gray-400 font-normal">({t('annotationForm.emotion.optional')})</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {EMOTION_LABELS.map((emotion) => (
                <button
                  key={emotion.value}
                  type="button"
                  onClick={() => setEmotionLabel(emotionLabel === emotion.value ? '' : emotion.value)}
                  className={`
                    px-3 py-1.5 rounded-full text-sm transition-all
                    ${
                      emotionLabel === emotion.value
                        ? 'bg-primary-100 text-primary-700 border-primary-300 border'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }
                  `}
                >
                  {emotion.label}
                </button>
              ))}
            </div>
          </div>

          {/* Intent Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('annotationForm.intent.label')} <span className="text-gray-400 font-normal">({t('annotationForm.intent.multiSelectHint')})</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {INTENT_OPTIONS.map((intent) => (
                <button
                  key={intent.value}
                  type="button"
                  onClick={() => handleIntentToggle(intent.value)}
                  className={`
                    px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1
                    ${
                      intents.includes(intent.value)
                        ? 'bg-blue-100 text-blue-700 border-blue-300 border'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }
                  `}
                >
                  {intents.includes(intent.value) && <Check className="w-3 h-3" />}
                  {intent.label}
                </button>
              ))}
            </div>
          </div>

          {/* Needs Support */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={needsSupport}
                onChange={(e) => setNeedsSupport(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">{t('annotationForm.needsSupport.label')}</span>
              <span className="text-gray-400 text-sm">({t('annotationForm.needsSupport.hint')})</span>
            </label>
          </div>
        </>
      )}

      {/* Confidence - show in both steps */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('annotationForm.confidence.label')} <span className="text-gray-400 font-normal">({t('annotationForm.emotion.optional')})</span>
        </label>
        <div className="flex gap-3">
          {[
            { value: 'high', labelKey: 'high', descKey: 'highDesc' },
            { value: 'medium', labelKey: 'medium', descKey: 'mediumDesc' },
            { value: 'low', labelKey: 'low', descKey: 'lowDesc' },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setConfidence(confidence === option.value ? '' : (option.value as ConfidenceLevel))}
              className={`
                flex-1 p-2 rounded-lg border transition-all text-center
                ${
                  confidence === option.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <div className={`font-medium ${confidence === option.value ? 'text-primary-700' : 'text-gray-700'}`}>
                {t(`annotationForm.confidence.${option.labelKey}`)}
              </div>
              <div className="text-xs text-gray-500">{t(`annotationForm.confidence.${option.descKey}`)}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('annotationForm.notes.label')} <span className="text-gray-400 font-normal">({t('annotationForm.emotion.optional')})</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('annotationForm.notes.placeholder')}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onSkip} disabled={isSubmitting}>
            <X className="w-4 h-4 mr-2" />
            {t('annotationForm.actions.skip')} <span className="text-gray-400 ml-1">(Esc)</span>
          </Button>

          {step === 2 && (
            <Button variant="secondary" onClick={() => setStep(1)} disabled={isSubmitting}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('annotationForm.actions.previous')}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-500 flex items-center gap-1">
            <HelpCircle className="w-4 h-4" />
            {step === 1 ? t('annotationForm.actions.continueHint') : t('annotationForm.actions.submitHint')}
          </div>

          {step === 1 && requiresEmotionAnnotation ? (
            <Button variant="primary" onClick={() => setStep(2)} disabled={!isStep1Valid}>
              {t('annotationForm.actions.next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              loading={isSubmitting}
            >
              <Check className="w-4 h-4 mr-2" />
              {t('annotationForm.actions.submitLabel')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
